import { useSearchEngine } from "$components/autocomplete/use-search-engine";
import { Button } from "$components/button/button";
import { PlusIcon } from "$components/icons";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  type Disease,
  RXDB_DISEASE_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/disease";
import { useRxDBContext } from "$global-providers/rxdb-provider/provider";
import { useUser } from "$global-providers/user-provider";
import { useClinicalDecisionSupportService } from "$routes/patient/[:patientId]/_lib/clinical-decision-support-service/provider";
import { addRelevantFormDiagnosesToCDS } from "$routes/patient/[:patientId]/_lib/form-investigation-to-cds";
import type { ActiveDiseaseOption } from "$routes/patient/[:patientId]/consultation/_lib/diagnosis-list-option";
import RenderDiagnosesListItem from "$routes/patient/[:patientId]/consultation/_lib/render-diagnoses-list-item";
import { Divider } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Fragment } from "react";
import { FormattedMessage } from "react-intl";
import { useRxData } from "rxdb-hooks";
import { v4 } from "uuid";
import { GuidedDiagnosisChip } from "./guided-diagnosis-chip";
import { defaultDiagnosis } from "./initial-form-default-values";
import { FormValues } from "./validation-schema";

const useStyles = makeStyles((theme) => ({
  divider: {
    marginBottom: theme.spacing(3),
  },
}));

const FUSE_OPTIONS = {
  keys: [
    {
      name: "keywords",
      weight: 0.3,
    },
    { name: "displayName", weight: 0.7 },
  ],
};

// Hard-coding categories from the diseases resolver done in api-consultatioj diseases resolver
// TODO ENG-4642: create an rxdb collection for categories
const CATEGORIES_OPTIONS = [
  {
    displayName:
      "Symptoms, signs or clinical findings, not elsewhere classified",
    value: "M",
  },
  {
    displayName: "Diseases of the digestive system",
    value: "D",
  },
  {
    displayName: "Diseases of the genitourinary system",
    value: "G",
  },
  {
    displayName: "Diseases of the ear or mastoid process",
    value: "A",
  },
  {
    displayName: "Diseases of the skin",
    value: "E",
  },
  {
    displayName: "Extension Codes",
    value: "X",
  },
  {
    displayName: "Diseases of the musculoskeletal system or connective tissue",
    value: "F",
  },
  {
    displayName: "Pregnancy, childbirth or the puerperium",
    value: "$",
  },
  {
    displayName: "Mental, behavioural or neurodevelopmental disorders",
    value: "6",
  },
  {
    displayName: "Diseases of the respiratory system    ",
    value: "C",
  },
  {
    displayName: "Certain infectious or parasitic diseases",
    value: "1",
  },
  {
    displayName: "Endocrine, nutritional or metabolic diseases",
    value: "5",
  },
  {
    displayName: "Diseases of the circulatory system",
    value: "B",
  },
  {
    displayName: "Neoplasms",
    value: "2",
  },
  {
    displayName: "Diseases of the visual system",
    value: "9",
  },
  {
    displayName: "Diseases of the immune system",
    value: "4",
  },
  {
    displayName:
      "Injury, poisoning or certain other consequences of external causes",
    value: "N",
  },
  {
    displayName: "Pregnancy, childbirth or the puerperium",
    value: "J",
  },
  {
    displayName: "Developmental anomalies",
    value: "L",
  },
  {
    displayName: "External causes of morbidity or mortality",
    value: "P",
  },
  {
    displayName: "Diseases of the blood or blood-forming organs",
    value: "3",
  },
  {
    displayName: "Diseases of the nervous system",
    value: "8",
  },
  {
    displayName:
      "Factors influencing health status or contact with health services",
    value: "Q",
  },
  {
    displayName: "Sleep-wake disorders",
    value: "7",
  },
  {
    displayName: "Codes for special purposes",
    value: "R",
  },
  {
    displayName: "Certain conditions originating in the perinatal period",
    value: "K",
  },
  {
    displayName: "Conditions related to sexual health",
    value: "H",
  },
  {
    displayName: "Supplementary Chapter Traditional Medicine Conditions",
    value: "S",
  },
];

export const DiagnosesSection: React.FC<{
  diagnoses: FormValues["diagnosis"];
  append: (diagnosis: FormValues["diagnosis"][number]) => void;
  remove: (index: number) => void;
  update: (index: number, diagnosis: FormValues["diagnosis"][number]) => void;
  shouldFocusLastDiagnosis?: boolean;
}> = ({
  diagnoses,
  append,
  remove,
  update,
  shouldFocusLastDiagnosis = false,
}) => {
  const [addVaccineFlowFlag] = useFeatureFlag(
    featureFlags.immunisations.addVaccineFlow,
  );

  const {
    replicationState: {
      disease: { isPulling: isDiseasePulling },
    },
  } = useRxDBContext();

  const { result: diseases, isFetching: isDiseasesFetching } =
    useRxData<Disease>(RXDB_DISEASE_COLLECTION_NAME, (collection) => {
      if (addVaccineFlowFlag) {
        return collection.find({
          selector: {
            $and: [
              {
                $or: [
                  {
                    "catalogueCoding.icd11.code": {
                      $not: { $regex: "^\\$IMM" },
                    },
                  },
                  { "catalogueCoding.icd11.code": { $exists: false } },
                ],
              },
              {
                $or: [
                  {
                    "catalogueCoding.icd10.code": {
                      $not: { $regex: "^\\$IMM" },
                    },
                  },
                  { "catalogueCoding.icd10.code": { $exists: false } },
                ],
              },
            ],
          },
        });
      }
      return collection.find();
    });

  const isLoading =
    isDiseasePulling || isDiseasesFetching || diseases.length === 0;

  const diseaseOptions = diseases.map((disease) => {
    const i18nEntry =
      disease._data.i18n.find((entry) => entry.locale === "en") ??
      disease._data.i18n[0];
    return {
      uuid: disease._data.id,
      category: disease._data.category,
      catalogueCoding: disease._data.catalogueCoding,
      displayName: i18nEntry.displayName,
      categoryDisplayName: i18nEntry.categoryDisplayName,
      keywords: i18nEntry.keywords ?? [],
    };
  });

  const searchEngine = useSearchEngine<ActiveDiseaseOption>(
    diseaseOptions,
    FUSE_OPTIONS,
  );
  const classes = useStyles();
  const user = useUser();

  const {
    checkDiagnosisSuggestion,
    patientClinicalResources: { add: addClinicalResource },
  } = useClinicalDecisionSupportService();
  const diagnosisGuidance = checkDiagnosisSuggestion();

  const handleSuggestedDiagnosisSelected = (icd11Code: string) => {
    const suggestedDisease = diseases.find(
      (disease) => disease.catalogueCoding.icd11.code === icd11Code,
    );

    if (!suggestedDisease) {
      return;
    }
    const diagnosisEntry: FormValues["diagnosis"][number] = {
      key: v4(),
      codification: {
        name: suggestedDisease.i18n[0].displayName,
        category:
          suggestedDisease.i18n[0].categoryDisplayName ||
          suggestedDisease.category,
        code:
          suggestedDisease.catalogueCoding?.icd11?.code ||
          suggestedDisease.catalogueCoding?.icd10?.code,
      },
      disease: suggestedDisease.id,
      freeText: false,
    };
    const emptyDiagnosisIndex = diagnoses.findIndex(
      (diagnosis) =>
        !diagnosis.codification.name ||
        diagnosis.codification.name.trim() === "",
    );

    if (emptyDiagnosisIndex !== -1) {
      update(emptyDiagnosisIndex, diagnosisEntry);
    } else {
      append(diagnosisEntry);
    }
    addRelevantFormDiagnosesToCDS({
      formItem: diagnosisEntry,
      userId: user.id,
      addClinicalResource,
    });
  };

  return (
    <ul>
      {diagnoses.map((diagnosis, index) => {
        return (
          <Fragment key={diagnosis.key}>
            {index > 0 && <Divider className={classes.divider} />}
            <RenderDiagnosesListItem
              index={index}
              searchEngine={searchEngine}
              remove={remove}
              categoryList={CATEGORIES_OPTIONS}
              isLoading={isLoading}
              shouldFocus={
                shouldFocusLastDiagnosis && index === diagnoses.length - 1
              }
            />
          </Fragment>
        );
      })}

      {diagnosisGuidance && (
        <li style={{ listStyle: "none", marginBottom: "16px" }}>
          <GuidedDiagnosisChip
            onDiagnosisSelected={() =>
              handleSuggestedDiagnosisSelected(diagnosisGuidance.icd11Code)
            }
          />
        </li>
      )}

      <Button
        variant="text"
        color="primary"
        startIcon={<PlusIcon />}
        onClick={() => {
          append(defaultDiagnosis());
        }}
      >
        <FormattedMessage
          id="diagnosis.addAnotherBtn"
          defaultMessage="Add another"
        />
      </Button>
    </ul>
  );
};
